/**
 * SocialStudio — AI social-media content studio (admin).
 *
 * Turn a topic (or, later, a job) into an optimized, platform-ready content
 * package: title, SEO description, tags, hashtags, a 0–15s hook script,
 * thumbnail text, a pinned-comment CTA, and a suggested local post time.
 * Talks only to /api/social (service-role, org-scoped) via apiFetch — the new
 * tables are deny-all, reachable only through that endpoint.
 *
 * Phase 1 (this): Generate + Library. Coming next: a refine chat copilot,
 * job-sourced briefs, and one-click publish/schedule to YouTube (+ IG/FB).
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", card: "#15181d", border: "#252a32", text: "#e8ebf0",
  muted: "#8892a4", accent: "#a5ce3a", danger: "#ef4444",
};

const PLATFORMS = [
  { key: "youtube_short", label: "YouTube Short" },
  { key: "youtube", label: "YouTube (long)" },
  { key: "instagram_reel", label: "Instagram Reel" },
  { key: "facebook", label: "Facebook" },
];
const PLATFORM_LABEL = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.label]));

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  if (!text) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1200); } catch { /* clipboard blocked */ }
  };
  return (
    <button onClick={copy} style={{
      background: "transparent", border: `1px solid ${C.border}`, color: done ? C.accent : C.muted,
      borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer",
    }}>{done ? "Copied ✓" : "Copy"}</button>
  );
}

function Field({ label, value }) {
  const empty = value == null || value === "" || (Array.isArray(value) && value.length === 0);
  if (empty) return null;
  const display = Array.isArray(value) ? value.join(", ") : String(value);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ color: C.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
        <CopyBtn text={display} />
      </div>
      <div style={{
        whiteSpace: "pre-wrap", color: C.text, fontSize: 14, lineHeight: 1.5,
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px",
      }}>{display}</div>
    </div>
  );
}

function ContentView({ content }) {
  if (!content) return null;
  const chapters = Array.isArray(content.chapters) && content.chapters.length
    ? content.chapters.map((c) => `${c.time || ""} — ${c.label || ""}`.trim()).join("\n")
    : "";
  return (
    <div>
      <Field label="Title" value={content.title} />
      <Field label="Description" value={content.description} />
      {chapters ? <Field label="Chapters" value={chapters} /> : null}
      <Field label="Hook (0–15s)" value={content.hook_script} />
      <Field label="Thumbnail text" value={content.thumbnail_text} />
      <Field label="Hashtags" value={content.hashtags} />
      <Field label="Tags" value={content.tags} />
      <Field label="Caption" value={content.caption} />
      <Field label="On-screen text" value={content.on_screen_text} />
      <Field label="Pinned comment" value={content.pinned_comment} />
      <Field label="Best time to post" value={content.best_time_local} />
      <Field label="Notes" value={content.notes} />
    </div>
  );
}

export default function SocialStudio() {
  const [tab, setTab] = useState("generate");
  const [platform, setPlatform] = useState("youtube_short");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [openId, setOpenId] = useState(null);

  const loadDrafts = useCallback(async () => {
    setLoadingList(true); setErr("");
    try {
      const r = await apiFetch("/api/social?action=list");
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Could not load");
      setDrafts(j.drafts || []);
    } catch (e) { setErr(e.message); }
    setLoadingList(false);
  }, []);

  useEffect(() => { if (tab === "library") loadDrafts(); }, [tab, loadDrafts]);

  const generate = async () => {
    if (!topic.trim()) { setErr("Tell me what the video is about."); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await apiFetch("/api/social", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "generate", platform, topic, notes }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Generation failed");
      setResult(j.draft?.content_json || null);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const del = async (id) => {
    try {
      const r = await apiFetch("/api/social", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete", id }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Delete failed");
      setOpenId((cur) => (cur === id ? null : cur));
      loadDrafts();
    } catch (e) { setErr(e.message); }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", background: C.bg, color: C.text,
    border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 14,
  };
  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{
      background: tab === key ? C.accent : "transparent", color: tab === key ? "#0d0f12" : C.muted,
      border: `1px solid ${tab === key ? C.accent : C.border}`, borderRadius: 8, padding: "6px 14px",
      fontSize: 13, fontWeight: 600, cursor: "pointer",
    }}>{label}</button>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100%", color: C.text, padding: 20 }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div style={{ marginBottom: 6 }}>
          <h1 style={{ fontSize: 24, margin: 0 }}>Social Studio</h1>
          <p style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            AI-optimized YouTube / social copy from a topic — titles, SEO descriptions, hashtags, hooks & post times.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, margin: "14px 0" }}>
          {tabBtn("generate", "Generate")}
          {tabBtn("library", "Library")}
        </div>

        {err ? (
          <div style={{ background: "#2a1416", border: `1px solid ${C.danger}`, color: "#ffd7d7", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>
            {err}
          </div>
        ) : null}

        {tab === "generate" ? (
          <div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <label style={{ color: C.muted, fontSize: 12, display: "block", marginBottom: 6 }}>Platform</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {PLATFORMS.map((p) => (
                  <button key={p.key} onClick={() => setPlatform(p.key)} style={{
                    background: platform === p.key ? C.accent : "transparent",
                    color: platform === p.key ? "#0d0f12" : C.text,
                    border: `1px solid ${platform === p.key ? C.accent : C.border}`,
                    borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer",
                  }}>{p.label}</button>
                ))}
              </div>

              <label style={{ color: C.muted, fontSize: 12, display: "block", marginBottom: 6 }}>
                What&apos;s the video about?
              </label>
              <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3}
                placeholder="e.g. Whole-home Generac standby generator demo — outage simulation, transfer switch, power + wifi stay on"
                style={{ ...inputStyle, resize: "vertical", marginBottom: 12 }} />

              <label style={{ color: C.muted, fontSize: 12, display: "block", marginBottom: 6 }}>
                Extra notes (optional)
              </label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                placeholder="Angle, key points to hit, the job/town, a call to action…"
                style={{ ...inputStyle, resize: "vertical", marginBottom: 14 }} />

              <button onClick={generate} disabled={busy} style={{
                background: busy ? C.border : C.accent, color: "#0d0f12", border: "none",
                borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 700,
                cursor: busy ? "default" : "pointer",
              }}>{busy ? "Writing…" : "Generate content"}</button>
            </div>

            {result ? (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
                <div style={{ color: C.accent, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>
                  {PLATFORM_LABEL[platform] || platform} — ready to post
                </div>
                <ContentView content={result} />
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            {loadingList ? (
              <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
            ) : drafts.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 13 }}>No saved content yet — generate something on the Generate tab.</div>
            ) : (
              drafts.map((d) => {
                const cj = d.content_json || {};
                const heading = cj.title || cj.caption || (cj.description || "").slice(0, 60) || "(untitled)";
                const open = openId === d.id;
                return (
                  <div key={d.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{heading}</div>
                        <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
                          {PLATFORM_LABEL[d.platform] || d.platform} · {d.status} · {String(d.created_at || "").slice(0, 10)}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                        <button onClick={() => setOpenId(open ? null : d.id)} style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>{open ? "Hide" : "View"}</button>
                        <button onClick={() => del(d.id)} style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.danger, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Delete</button>
                      </div>
                    </div>
                    {open ? <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}><ContentView content={cj} /></div> : null}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
